import {
  Component, EventEmitter, Input, OnInit, OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef, ChangeDetectorRef
} from '@angular/core';
import {
  interval, Observable, Subscription
} from "rxjs";
import {ActivatedRoute, ActivatedRouteSnapshot} from "@angular/router";
import {ErrorPanelComponent} from "../error-panel/error-panel.component";
import {JsonPipe, NgClass, NgIf, NgTemplateOutlet} from "@angular/common";
import {NgClassType} from "../../model/common.model";
import {ReflectedType, ReflectorService} from "../../services/reflector.service";
import {ComponentBase} from "../component-base/component-base.component";
import {NavigationStateService} from "../../services/navigation-state.service";


export type SrsLoaderType<TContent> = Observable<TContent>

export interface SrsLoaderOptions<TContent> {
  autoRefreshInterval?: number
  disableAutoRefresh?: boolean
  forceRefreshOnNavigationChange?: boolean
  context?: any
  outerNgClass?: NgClassType
  errorTemplate?: TemplateRef<{
    error: any,
    loadCounter?: number,
    context?: any}>
  loadingTemplate?: TemplateRef<{
    loading: boolean,
    loadCounter?: number,
    context?: any}>
  contentTemplate?: TemplateRef<{
    loadCounter?: number,
    content: TContent,
    reflectedType: ReflectedType
    context?: any}>
}

@Component({
  selector: 'loader-container',
  standalone: true,
  imports: [
    ErrorPanelComponent,
    JsonPipe,
    NgIf,
    NgTemplateOutlet,
    NgClass
  ],
  templateUrl: './loader-container.component.html'
})
export class LoaderContainerComponent<TContent> extends ComponentBase implements OnInit, OnChanges, OnDestroy {

  readonly defaultAutoRefreshInterval: number = 300*1000

  @Input() loader?: SrsLoaderType<TContent>
  @Input() options: SrsLoaderOptions<TContent> = {}
  @Output() error = new EventEmitter<any>()
  @Output() loading = new EventEmitter<{
    loading: boolean,
    loadCounter: number
  }>()
  @Output() loaded = new EventEmitter<TContent>()

  stateError: any = undefined
  stateLoading: boolean = false
  stateContent?: TContent = undefined
  stateLoadCounter: number = -1
  stateContentReflectedType: ReflectedType = 'undefined'
  navigationDepth: number = 0
  stateLoaderSubscription?: Subscription = undefined
  refreshSub?: Subscription
  loggingId: string = ''
  lastLoader?: SrsLoaderType<TContent>
  routerDepthChangeSub?: Subscription

  constructor(
    readonly navService: NavigationStateService,
    readonly reflector: ReflectorService,
    readonly route: ActivatedRoute,
    readonly cdr: ChangeDetectorRef
  ) {
    super()
    this.loggingId = this.uid || this.navService.buildResolvedUrl(this.route.snapshot)
    this.navigationDepth = this.navService.getNavigationDepth(this.route)
    console.debug(`[${this.loggingId}] Initializing at depth`, this.navigationDepth)
  }

  override ngOnInit() {
    this.loggingId = this.uid || this.navService.buildResolvedUrl(this.route.snapshot)
    super.ngOnInit()
    this.routerDepthChangeSub = this.navService.navigationDepthChange.subscribe(depth => {
      if (depth == this.navigationDepth) {
        console.debug(`[${this.loggingId}] Navigation depth has changed to our depth`, depth, 'Reloading!')
        this.reload()
      }
      this.cdr.detectChanges()
    })
    this.invalidate()
  }

  ngOnChanges(_: SimpleChanges): void {
    this.invalidate()
  }

  override ngOnDestroy(): void {
    this.unsubscribeRouterChange()
    this.unsubscribeLoader()
    this.unsubscribeAutoRefresh()
    super.ngOnDestroy()
  }

  protected unsubscribeLoader(): void {
    this.stateLoaderSubscription?.unsubscribe()
    this.stateLoaderSubscription = undefined
  }

  protected unsubscribeAutoRefresh(): void {
    this.refreshSub?.unsubscribe()
    this.refreshSub = undefined
  }

  protected unsubscribeRouterChange(): void {
    this.routerDepthChangeSub?.unsubscribe()
    this.routerDepthChangeSub = undefined
  }

  protected invalidate() {
    this.loggingId = this.uid || this.navService.buildResolvedUrl(this.route.snapshot)
    //
    // reset state
    //
    this.unsubscribeAutoRefresh()
    this.stateLoading = false
    this.stateLoadCounter = 0
    this.emitLoadingEvent()

    //
    // do initial load
    //
    this.loadData(this.loader)

    //
    // schedule auto-refresh
    //
    const refreshInterval = this.options.autoRefreshInterval || this.defaultAutoRefreshInterval
    if (refreshInterval > 0 && !this.options.disableAutoRefresh) {
      this.refreshSub = interval(refreshInterval).subscribe({
        next: () => {
          this.loadData(this.loader)
        }
      })
    }
    else {
      console.warn(
        `[${this.loggingId}] Skipping auto refresh, auto-refresh interval = ${refreshInterval}, `
        + `disableAutoRefresh = ${this.options.disableAutoRefresh}`)
    }
  }

  protected emitLoadingEvent(): void {
    this.loading.emit({
      loading: this.stateLoading,
      loadCounter: this.stateLoadCounter,
    })
  }

  protected loadData(loader?: SrsLoaderType<TContent>): void {
    this.unsubscribeLoader()
    console.debug(
      `[${this.loggingId}] Load invoked`,
      'our depth', this.navigationDepth,
      'global depth', this.navService.navigationDepth
    )
    if (this.navigationDepth === this.navService.navigationDepth || this.options?.forceRefreshOnNavigationChange === true) {
      this.stateError = undefined
      this.stateLoading = false
      this.stateContent = undefined
      if (loader) {
        console.debug(`[${this.loggingId}] Auto-refreshing, navigation depth is `,
          this.navService.navigationDepth)
        this.lastLoader = loader
        this.stateLoading = true
        this.stateLoadCounter++
        this.emitLoadingEvent()
        this.stateLoaderSubscription = loader.subscribe({
          next: (r) => {
            this.stateLoading = false
            this.stateContent = r
            this.stateContentReflectedType = this.reflector.getReflectedType(r)
            this.loaded.next(r)
            this.emitLoadingEvent()
          },
          error: (e) => {
            this.stateLoading = false
            this.stateError = e
            this.error.next(e)
            this.emitLoadingEvent()
            console.error(`[${this.loggingId}] Failed to load content`, e, 'Sender', this)
          }
        })
      }
    }
    else {
      console.warn(
        `[${this.loggingId}] Skipping auto refresh, depth is different;`,
        'our depth', this.navigationDepth,
        'global depth', this.navService.navigationDepth)
    }
  }

  reload(): void {
    this.loadData(this.loader)
  }
}
